import { AsyncPipe, KeyValue } from '@angular/common';
import {
    ChangeDetectorRef,
    Component,
    ElementRef,
    Input,
    OnChanges,
    OnInit,
    SimpleChanges,
    ViewChild,
} from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { TranslateModule, TranslatePipe } from '@ngx-translate/core';
import { SewTableHeaderFilter } from '@sew/ui-components-angular/standalone';
import { from, merge, switchMap, tap } from 'rxjs';
import { isDefined } from '../../../../../helpers/type-guards/guards';
import { TableFilterService } from '../../../services/table-filter/table-filter.service';
import { SortOrder } from '../../../services/table-sort/table-sort.interface';
import { TableSortService } from '../../../services/table-sort/table-sort.service';
import { FilterTableHeaderComponent } from '../filter-table-header.component';
import { FilterTypes } from '../filter.interface';

@Component({
    selector: 'osc-selection-filter-table-header',
    templateUrl: './selection-filter-table-header.component.html',
    styleUrls: ['./selection-filter-table-header.component.scss'],
    imports: [
        ReactiveFormsModule,
        FormsModule,
        AsyncPipe,
        TranslatePipe,
        SewTableHeaderFilter,
        TranslateModule,
    ],
})
export class SelectionFilterTableHeaderComponent<T, G, E>
    extends FilterTableHeaderComponent<T, G, E>
    implements OnInit, OnChanges
{
    @Input() public name?: string;
    @Input() public ellipsis?: boolean = false;
    @Input() public filterProperty?: keyof T & string;
    @Input() public tagPosition?: number;
    @Input() public filterValues: KeyValue<string, string | boolean | undefined>[] = [];
    @Input() public set sortOrder(sortOrder: SortOrder) {
        this.sortOrderInput.next(sortOrder);
        this.order = sortOrder;
    }

    @Input() public filterType: FilterTypes = FilterTypes.BOOLEAN_FILTER;
    @Input() public hasReset = false;
    @Input() public hideSortIconOnClosedState = false;

    public filterTypes = FilterTypes;

    @ViewChild('sewTableHeaderFilter') private tableHeaderFilter?: ElementRef<SewTableHeaderFilter>;

    public constructor(
        public filterService: TableFilterService<T, G, E>,
        public sortService: TableSortService<T, G, E>,
        private changeDectRef: ChangeDetectorRef
    ) {
        super(filterService, sortService);
    }

    public ngOnInit(): void {
        if (!this.filterProperty) {
            return console.warn('Trying to initialize a text filter without filterProperty');
        }

        this.filterValue = this.filterService.watchPropertyValue(this.filterProperty).pipe(
            // WORKAROUND needed because ngmodel sometimes doesn't detect change on itself
            switchMap((value) => from([null as any, value])),
            tap((value) => {
                if (value === '') {
                    this.tableHeaderFilter?.nativeElement?.resetValue();
                } else if (isDefined(value)) {
                    this.tableHeaderFilter?.nativeElement?.setValue(value.toString());
                }
                this.changeDectRef.detectChanges();
            })
        );

        this.sortOrderObs = merge(
            this.sortOrderInput,
            this.sortService.watchPropertyValue(this.filterProperty)
        );
    }

    public ngOnChanges(changes: SimpleChanges) {
        if (changes.filterProperty || changes.sortOrder) {
            if (this.filterProperty && this.order) {
                this.sortService.inititizeSorting = () => {
                    if (this.order) {
                        this.updateSorting(this.order);
                    }
                };
            }
        }
    }
}
